import React, { useEffect, useState, useCallback } from 'react';
import {
  getAgentTasks,
  getAgentRuns,
  getAgentStats,
  updateAgentTask,
  executeAgentTask,
} from '../api';

// Warm earth palette values — match CSS custom properties
const AGENT_TYPE_COLORS = {
  growth:     '#355E3B', // --leaf
  moderation: '#D9852C', // --citrus
  analytics:  '#8B6B4A', // --soil
  content:    '#6B8F52', // --herb
};

const PRIORITY_COLORS = {
  low:    '#8A9187', // --ink-mute
  normal: '#4A5247', // --ink-mid
  high:   '#D9852C', // --citrus
  urgent: '#c53030', // error red
};

const STATUS_COLORS = {
  proposed:  '#D9852C', // --citrus
  approved:  '#355E3B', // --leaf
  rejected:  '#c53030', // error red
  completed: '#355E3B', // --leaf
  failed:    '#c53030', // error red
  executing: '#6B8F52', // --herb
};

const FILTER_TABS = ['all', 'proposed', 'approved', 'rejected', 'completed', 'failed'];

export default function AgentsPage() {
  const [tasks, setTasks] = useState([]);
  const [runs, setRuns] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeFilter, setActiveFilter] = useState('all');
  const [actionLoading, setActionLoading] = useState({});

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { limit: '50', offset: '0' };
      if (activeFilter !== 'all') params.status = activeFilter;

      const [tasksData, runsData, statsData] = await Promise.all([
        getAgentTasks(params),
        getAgentRuns({ limit: '10' }),
        getAgentStats(),
      ]);

      setTasks(tasksData.tasks || []);
      setRuns(runsData.runs || []);
      setStats(statsData);
    } catch (err) {
      setError(err.message || 'Failed to load agent data');
    } finally {
      setLoading(false);
    }
  }, [activeFilter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleApprove = async (taskId) => {
    setActionLoading((prev) => ({ ...prev, [taskId]: 'approving' }));
    try {
      await updateAgentTask(taskId, { status: 'approved' });
      setTasks((prev) =>
        prev.map((t) => (t.id === taskId ? { ...t, status: 'approved' } : t))
      );
    } catch (err) {
      alert('Failed to approve task: ' + (err.message || 'Unknown error'));
    } finally {
      setActionLoading((prev) => ({ ...prev, [taskId]: null }));
    }
  };

  const handleReject = async (taskId) => {
    setActionLoading((prev) => ({ ...prev, [taskId]: 'rejecting' }));
    try {
      await updateAgentTask(taskId, { status: 'rejected' });
      setTasks((prev) =>
        prev.map((t) => (t.id === taskId ? { ...t, status: 'rejected' } : t))
      );
    } catch (err) {
      alert('Failed to reject task: ' + (err.message || 'Unknown error'));
    } finally {
      setActionLoading((prev) => ({ ...prev, [taskId]: null }));
    }
  };

  const handleExecute = async (taskId) => {
    setActionLoading((prev) => ({ ...prev, [taskId]: 'executing' }));
    try {
      await executeAgentTask(taskId);
      setTasks((prev) =>
        prev.map((t) => (t.id === taskId ? { ...t, status: 'executing' } : t))
      );
    } catch (err) {
      alert('Failed to execute task: ' + (err.message || 'Unknown error'));
    } finally {
      setActionLoading((prev) => ({ ...prev, [taskId]: null }));
    }
  };

  const formatTimestamp = (ts) => {
    if (!ts) return '--';
    const d = new Date(ts);
    return (
      d.toLocaleDateString('en-ZA') +
      ' ' +
      d.toLocaleTimeString('en-ZA', { hour: '2-digit', minute: '2-digit' })
    );
  };

  return (
    <div>
      <div className="page-header page-header-row">
        <div>
          <h1>Agent Tasks</h1>
          <p>Manage autonomous agent proposals and executions</p>
        </div>
        <button
          className="btn btn-secondary"
          onClick={fetchData}
          disabled={loading}
          style={{ marginTop: 4 }}
        >
          {loading ? 'Refreshing...' : 'Refresh'}
        </button>
      </div>

      {/* Stats Bar */}
      {stats && (
        <div className="stats-bar">
          <div className="agent-stat-item">
            <div className="agent-stat-value">{stats.total ?? 0}</div>
            <div className="agent-stat-label">Total Proposed</div>
          </div>
          <div className="agent-stat-item">
            <div className="agent-stat-value" style={{ color: STATUS_COLORS.proposed }}>
              {stats.pending ?? 0}
            </div>
            <div className="agent-stat-label">Pending Approval</div>
          </div>
          <div className="agent-stat-item">
            <div className="agent-stat-value" style={{ color: STATUS_COLORS.approved }}>
              {stats.approved ?? 0}
            </div>
            <div className="agent-stat-label">Approved</div>
          </div>
          <div className="agent-stat-item">
            <div className="agent-stat-value" style={{ color: STATUS_COLORS.completed }}>
              {stats.executed ?? 0}
            </div>
            <div className="agent-stat-label">Executed</div>
          </div>
          <div className="agent-stat-item">
            <div className="agent-stat-value" style={{ color: STATUS_COLORS.failed }}>
              {stats.failed ?? 0}
            </div>
            <div className="agent-stat-label">Failed</div>
          </div>
        </div>
      )}

      {/* Filter Tabs */}
      <div className="filter-bar">
        {FILTER_TABS.map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveFilter(tab)}
            className={`filter-tab${activeFilter === tab ? ' active' : ''}`}
          >
            {tab.charAt(0).toUpperCase() + tab.slice(1)}
          </button>
        ))}
      </div>

      {/* Error State */}
      {error && (
        <div className="error-box">
          <strong>Error:</strong> {error}
          <button onClick={fetchData}>Retry</button>
        </div>
      )}

      {/* Loading / Empty / Task Cards */}
      {loading ? (
        <div className="loading">Loading agent tasks...</div>
      ) : !error && tasks.length === 0 ? (
        <div className="empty-state">
          <p>
            No agent tasks found
            {activeFilter !== 'all' ? ` with status "${activeFilter}"` : ''}.
          </p>
        </div>
      ) : !error && (
        <div className="task-grid">
          {tasks.map((task) => (
            <div key={task.id} className="task-card">
              <div className="task-header">
                <div className="task-header-left">
                  <span
                    className="agent-badge"
                    style={{ backgroundColor: AGENT_TYPE_COLORS[task.agentType] || '#8A9187' }}
                  >
                    {task.agentType || 'unknown'}
                  </span>
                  <span
                    className="agent-badge-outline"
                    style={{ color: PRIORITY_COLORS[task.priority] || '#8A9187' }}
                  >
                    {task.priority || 'normal'}
                  </span>
                </div>
                <span
                  className="agent-badge"
                  style={{ backgroundColor: STATUS_COLORS[task.status] || '#8A9187' }}
                >
                  {task.status}
                </span>
              </div>

              <h3 className="task-title">{task.title || 'Untitled Task'}</h3>
              <p className="task-description">{task.description || 'No description provided.'}</p>
              <div className="task-timestamp">
                {formatTimestamp(task.createdAt || task.created_at)}
              </div>

              <div className="task-actions">
                {task.status === 'proposed' && (
                  <>
                    <button
                      className="action-btn action-btn-approve"
                      onClick={() => handleApprove(task.id)}
                      disabled={!!actionLoading[task.id]}
                    >
                      {actionLoading[task.id] === 'approving' ? 'Approving...' : 'Approve'}
                    </button>
                    <button
                      className="action-btn action-btn-reject"
                      onClick={() => handleReject(task.id)}
                      disabled={!!actionLoading[task.id]}
                    >
                      {actionLoading[task.id] === 'rejecting' ? 'Rejecting...' : 'Reject'}
                    </button>
                  </>
                )}
                {task.status === 'approved' && (
                  <button
                    className="action-btn action-btn-execute"
                    onClick={() => handleExecute(task.id)}
                    disabled={!!actionLoading[task.id]}
                  >
                    {actionLoading[task.id] === 'executing' ? 'Executing...' : 'Execute'}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Recent Runs Section */}
      {!loading && !error && runs.length > 0 && (
        <div className="runs-section">
          <h2 className="section-title">Recent Runs</h2>
          <div className="card">
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th>Agent</th>
                    <th>Task</th>
                    <th>Status</th>
                    <th>Started</th>
                    <th>Duration</th>
                    <th>Result</th>
                  </tr>
                </thead>
                <tbody>
                  {runs.map((run) => (
                    <tr key={run.id}>
                      <td>
                        <span
                          className="agent-badge"
                          style={{
                            backgroundColor: AGENT_TYPE_COLORS[run.agentType] || '#8A9187',
                            fontSize: 11,
                          }}
                        >
                          {run.agentType || 'unknown'}
                        </span>
                      </td>
                      <td className="run-task-cell">
                        {run.taskTitle || run.task_id || '--'}
                      </td>
                      <td>
                        <span
                          className="agent-badge"
                          style={{
                            backgroundColor: STATUS_COLORS[run.status] || '#8A9187',
                            fontSize: 11,
                          }}
                        >
                          {run.status}
                        </span>
                      </td>
                      <td className="run-time-cell">
                        {formatTimestamp(run.startedAt || run.started_at)}
                      </td>
                      <td className="run-time-cell">
                        {run.durationMs != null
                          ? `${(run.durationMs / 1000).toFixed(1)}s`
                          : '--'}
                      </td>
                      <td className="run-result-cell">
                        {run.result || run.error || '--'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
